import LunchScannerInstructions from 'scenes/home/lunchScanner/components/Instructions'
import LoadingComponent from 'components/atoms/Loading'
import { Pressable, ScrollView, StyleSheet, View } from 'react-native'
import UserAvatar from 'components/atoms/UserAvatar'
import { ThemedSizes } from 'styles/themed'
import Text from "theme/Text";
import Timeslot from 'components/molecules/Timeslot'
import HelperString from 'utils/helper-string'
import i18n from 'utils/i18n'
import React, { useMemo } from 'react'
import { SCAN_STATUS } from 'scenes/home/lunchScanner/index'
import _ from 'lodash'
import { colorList } from 'utils/colors'
import HelperDate from 'utils/helper-date'
import VStack from 'components/atoms/stack/VStack'

const LunchScannerResults = ( { status, checkIn, reset, employeeBookingDb, compact = false }) => {
	
	const timeslot = useMemo( () => {
		if( employeeBookingDb ) {
			return employeeBookingDb.timeslot ?? {}
		}
		
		return {}
	}, [ employeeBookingDb ] );
	
	const building = useMemo( () => {
		if( employeeBookingDb ) {
			return employeeBookingDb.building ?? { name: '-' }
		}
		
		return {
			name: '-'
		}
	}, [ employeeBookingDb ] );
	
	const timeslotKind = {
		kind: _.get( timeslot, 'kind' ),
		sub_kind: _.get( timeslot, 'sub_kind' )
	};
	
	const fakeBooking = {
		label: _.get( building, 'name', '-' ),
		timeslot: {
			...timeslotKind,
			timeslot_start: _.get( timeslot, 'timeslot_start'),
			timeslot_end: _.get( timeslot, 'timeslot_end')
		}
	};
	
	const getI18nValidByTime = () => {
		// get the hour
		const hour = HelperDate.date().format( 'HH' );
		
		if ( hour >= 12 && hour < 18 ) {
			return 'lunch_scanner.index.status.VALID.afternoon';
		} else if ( hour >= 18 && hour < 24 ) {
			return 'lunch_scanner.index.status.VALID.evening';
		} else if ( hour >= 0 && hour < 6 ) {
			return 'lunch_scanner.index.status.VALID.night';
		} else {
			return 'lunch_scanner.index.status.VALID.morning';
		}
	}
	
	const Container = ScrollView;
	const containerProps = {
		style: styles.scroll,
		contentContainerStyle: [ styles.content, compact && styles.contentCompact ],
	};
	return (
		<Container { ...containerProps }>
			
			{ status === SCAN_STATUS.PENDING && (
				<LunchScannerInstructions compact={ compact } />
			)}
			
			{ status === SCAN_STATUS.LOADING && (
				<LoadingComponent size="l" />
			)}
			
			{ (status === SCAN_STATUS.VALIDATE && employeeBookingDb ) && (
				<VStack style={ styles.validation }>
					<View style={ styles.employeeContainer }>
						<View style={ styles.employeeAvatar}>
							<UserAvatar
								employee={ employeeBookingDb?.employee }
								size={ ThemedSizes.L }
							/>
						</View>
						
						<View style={ styles.employeeName }>
							<Text category="h4" style={ styles.employeeNameText}>{ employeeBookingDb?.employee?.name }</Text>
							
							<View style={ styles.employeeTimeslot }>
								<Timeslot
									key={ HelperString.hash( 'validate-booking-timeslot' ) }
									item={ fakeBooking }
									itemProps={{
										showCapacity: false
									}}
									timeslotKind={ timeslotKind }
									timeslotIconOverWriteProperties={ timeslotKind }
								/>
							</View>
						</View>
					</View>
					
					<View style={ styles.buttonsContainer }>
						<Pressable accessibilityRole="button" onPress={ checkIn }>
							<View style={ [ styles.button, styles.buttonCheckin, compact && styles.buttonCompact ] }>
								<Text style={ [ styles.buttonText, styles.buttonTextCheckin ] }>{ i18n.t( 'lunch_scanner.index.buttons.checkin') }</Text>
							</View>
						</Pressable>
						<Pressable accessibilityRole="button" onPress={ reset }>
							<View style={ [ styles.button, styles.buttonReset, compact && styles.buttonCompact ] }>
								<Text style={ [ styles.buttonText, styles.buttonTextReset ] }>{ i18n.t( 'lunch_scanner.index.buttons.cancel') }</Text>
							</View>
						</Pressable>
					</View>
				</VStack>
			)}
			
			{ status === SCAN_STATUS.VALID && (
				<Pressable accessibilityRole="button" style={ styles.validation } onPress={ reset }>
					<View style={ [ styles.result, styles.resultSuccess ] }>
						<View>
							<Text style={ styles.resultText }>{ i18n.t( 'lunch_scanner.index.status.VALID.intro', { name: employeeBookingDb?.employee?.name }) }</Text>
						</View>
						<View>
							<Text style={ styles.resultText }>{ i18n.t( getI18nValidByTime(), { name: employeeBookingDb?.employee?.name } ) }</Text>
						</View>
					</View>
				</Pressable>
			)}
			
			{ [ SCAN_STATUS.INVALID, SCAN_STATUS.WRONG_DAY, SCAN_STATUS.ALREADY_CHECKED_IN ].includes( status ) && (
				<Pressable accessibilityRole="button" style={ styles.validation } onPress={ reset }>
					<View style={ [ styles.result, styles.resultError ] }>
						<Text style={ styles.resultText }>{ i18n.t( `lunch_scanner.index.status.${status}` ) }</Text>
					</View>
				</Pressable>
			)}
		</Container>
	)
};

const styles = StyleSheet.create({
	scroll: { flex: 1, width: '100%' },
	content: { flexGrow: 1, justifyContent: 'center', padding: 24 },
	contentCompact: { padding: 12 },
	validation: { width: '100%', maxWidth: 640, alignSelf: 'center' },
	employeeContainer: {
		minHeight: 140,
		flexDirection: 'row',
		backgroundColor: colorList.WHITE.primary,
		padding: 16,
		gap: 16,
		borderRadius: 20,
	},
	employeeAvatar: { justifyContent: 'center', alignItems: 'center' },
	employeeName: { flex: 1, minWidth: 0, justifyContent: 'center', alignItems: 'flex-start' },
	employeeNameText: { color: colorList.BLACK.dark, textAlign: 'left' },
	employeeTimeslot: { marginTop: 12, width: '100%' },
	buttonsContainer: { marginTop: 20, gap: 12 },
	button: {
		width: '100%',
		minHeight: 64,
		padding: 16,
		borderRadius: 16,
		alignItems: 'center',
		justifyContent: 'center',
	},
	buttonCompact: { minHeight: 48, padding: 12 },
	buttonCheckin: { backgroundColor: colorList.GREEN.dark },
	buttonReset: { backgroundColor: colorList.WHITE.primary },
	buttonText: { fontSize: 22, fontWeight: '700', textAlign: 'center' },
	buttonTextCheckin: { color: colorList.WHITE.primary },
	buttonTextReset: { color: colorList.TEXT.primary, fontSize: 18 },
	result: {
		width: '100%',
		minHeight: 180,
		padding: 24,
		gap: 16,
		borderRadius: 20,
		alignItems: 'center',
		justifyContent: 'center',
	},
	resultText: { color: colorList.WHITE.primary, fontSize: 28, fontWeight: '700', textAlign: 'center' },
	resultError: { backgroundColor: colorList.RED.dark },
	resultSuccess: { backgroundColor: colorList.GREEN.dark },
});

export default LunchScannerResults;
